import { message } from 'antd';
import PropTypes from 'prop-types';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { client } from '../../actions';
import { authHeader } from '../../helpers/auth-header';
import getAttachmentType from '../../helpers/getAttachmentType';
import { getAttachmentNameWithExtension } from '../../helpers/getAttachmentNameWithExtension';
import { Lightbox } from '../../modal_image';

const LightboxGroup = ({
  attachments,
  visible,
  attachment,
  onClose,
  loader,
  url,
  params,
  imageParamField,
}) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const previews = useMemo(
    () =>
      attachments.filter(
        (file) => getAttachmentType(getAttachmentNameWithExtension(file)) === 'picture'
      ),
    [attachments]
  );
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [fileName, setFileName] = useState('');
  const cache = useRef([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const loaderFunc = useCallback(
    async (fileName) => {
      const response = await client.get(url, {
        params: { ...params, [imageParamField]: fileName },
        headers: authHeader(),
      });

      if (response?.status === 200) {
        const data = Buffer.from(response.data, 'base64');
        return window.URL.createObjectURL(new Blob([data]));
      }
    },
    [imageParamField, params, url]
  );

  const getPreview = useCallback(
    async (index) => {
      setLoadingPreview(true);
      let data;
      try {
        if (!cache.current[index]) {
          if (loader) {
            data = await loader(previews[index].fileName || previews[index]);
          } else {
            data = await loaderFunc(previews[index].fileName);
          }
          cache.current[index] = data;
        } else {
          data = cache.current[index];
        }
        setPreviewUrl(data);
        setFileName(previews[index].name);
        setCurrentPreviewIndex(index);
      } catch (error) {
        onClose();
        message.error('Не удалось загрузить изображение');
        console.error(error);
      }
      setLoadingPreview(false);
    },
    [previews, loaderFunc, loader]
  );

  useEffect(() => {
    if (visible) {
      (async () => {
        await getPreview(
          previews.findIndex((attach) => attach.id === attachment.id)
        );
      })();
    }
  }, [visible, getPreview, previews, attachment]);

  const handlePrevPreview = useCallback(async () => {
    let index = currentPreviewIndex;
    if (currentPreviewIndex > 0) {
      index = currentPreviewIndex - 1;
    } else {
      index = previews.length - 1;
    }
    await getPreview(index);
  }, [currentPreviewIndex, previewUrl, getPreview]);

  const handleNextPreview = useCallback(async () => {
    let index = currentPreviewIndex;

    if (currentPreviewIndex < previews.length - 1) {
      index = currentPreviewIndex + 1;
    } else {
      index = 0;
    }
    await getPreview(index);
  }, [currentPreviewIndex, previewUrl, getPreview]);

  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (previews.length > 1) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          await handleNextPreview();
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          await handlePrevPreview();
        }
      }
    };
    if (visible && attachment) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [handleNextPreview, handlePrevPreview]);

  if (!visible || !attachment) {
    return null;
  }
  return (
    <Lightbox
      loading={loadingPreview}
      medium={previewUrl}
      large={previewUrl}
      alt={fileName}
      showRotate={true}
      onClose={onClose}
      onPrev={previews?.length > 1 && handlePrevPreview}
      onNext={previews?.length > 1 && handleNextPreview}
    />
  );
};

LightboxGroup.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  visible: PropTypes.bool,
  attachment: PropTypes.oneOfType([
    PropTypes.shape({
      fileName: PropTypes.string,
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }),
    PropTypes.bool,
  ]),
  onClose: PropTypes.func.isRequired,
  loader: (props, propName, componentName) => {
    if (props.url || props.params || props.imageParamField) {
      return new Error(
        `В ${componentName} передается ${propName}, 
        параметры url, params, imageParamField будут игнорироваться`
      );
    }
    if (
      !(props.url || props.params || props.imageParamField) &&
      typeof props.loader !== 'function'
    ) {
      return new Error(`${propName} в ${componentName} должен быть функцией`);
    }
  },
  url: (props, propName, componentName) => {
    if (!props.url && (props.params || props.imageParamField)) {
      return new Error(
        `В ${componentName} не передается ${propName}, 
        параметры url && params && imageParamField или loader обязательны`
      );
    }
    if (!props.loader && typeof props.url !== 'string') {
      return new Error(`${propName} в ${componentName} должен быть строкой`);
    }
  },
  params: (props, propName, componentName) => {
    if (!props.params && (props.url || props.imageParamField)) {
      return new Error(
        `В ${componentName} не передается ${propName}, 
        параметры url && params && imageParamField или loader обязательны`
      );
    }
    if (!props.loader && !(typeof props.params === 'null' && !!props.params)) {
      return new Error(`${propName} в ${componentName} должен быть объектом`);
    }
  },
  imageParamField: (props, propName, componentName) => {
    if (!props.imageParamField && (props.url || props.params)) {
      return new Error(
        `В ${componentName} не передается ${propName}, 
        параметры url && params && imageParamField или loader обязательны`
      );
    }
    if (!props.loader && typeof props.imageParamField !== 'string') {
      return new Error(`${propName} в ${componentName} должен быть строкой`);
    }
  },
};

export default memo(LightboxGroup);
